import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  Avatar,
  Paper,
  Chip,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import {
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  CallEnd,
  ScreenShare,
  StopScreenShare,
  VolumeUp,
  VolumeOff,
  Settings,
  Refresh,
  CameraAlt,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import timezoneService from '../services/timezoneService';

interface HuddleModalProps {
  open: boolean;
  onClose: () => void;
  channelId: string;
  channelName: string;
  participants?: any[]; // Optional - will fetch internally if not provided
}

interface Participant {
  id: string;
  name: string;
  avatar?: string;
  isAudioEnabled: boolean;
  isVideoEnabled: boolean;
  isScreenSharing: boolean;
  stream?: MediaStream;
  connection?: RTCPeerConnection;
  isActive: boolean;
  isCurrentUser: boolean;
}

interface SignalingMessage {
  id: string;
  type: 'offer' | 'answer' | 'ice-candidate' | 'join' | 'leave' | 'media-state';
  channelId: string;
  fromUserId: string;
  toUserId?: string;
  data?: any;
  timestamp: Date;
}

// Default WebRTC Configuration with STUN servers only (TURN loaded from backend)
const defaultRtcConfig: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
  iceCandidatePoolSize: 10,
  iceTransportPolicy: 'all',
};

export const HuddleModal: React.FC<HuddleModalProps> = ({
  open,
  onClose,
  channelId,
  channelName,
  participants: initialParticipants = [], // Default to empty array if not provided
}) => {
  const { state: authState } = useAuth();
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isSpeakerEnabled, setIsSpeakerEnabled] = useState(true);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string>('');
  const [availableDevices, setAvailableDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>('');
  const [connectionStatus, setConnectionStatus] = useState<string>('Disconnected');
  const [streamStatus, setStreamStatus] = useState<string>('Not initialized');
  const [mediaConstraints, setMediaConstraints] = useState<any>(null);

  const rtcConfig = useRef<RTCConfiguration>(defaultRtcConfig);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRefs = useRef<{ [key: string]: HTMLVideoElement }>({});
  const peerConnections = useRef<{ [key: string]: RTCPeerConnection }>({});
  const iceCandidateBuffer = useRef<{ [key: string]: RTCIceCandidateInit[] }>({}); // Buffer ICE candidates until remote description is set
  const processedMessageIds = useRef<Set<string>>(new Set()); // Track processed messages to prevent duplicates
  const makingOffer = useRef<{ [key: string]: boolean }>({}); // Track if we're currently making an offer (for perfect negotiation)
  const streamSetupRef = useRef<boolean>(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastPollTime = useRef<Date>(
    // eslint-disable-next-line no-restricted-syntax
    new Date()
  );
  const retryCount = useRef<number>(0);
  const maxRetries = useRef<number>(5);
  const isReconnecting = useRef<boolean>(false);
  const lastConnectionStatus = useRef<string>('Disconnected');

  const addDebugInfo = useCallback((message: string) => {
    // Get current time in user's timezone and format
    // eslint-disable-next-line no-restricted-syntax
    const now = new Date(); // Current time
    const timestamp = timezoneService.formatUserTime(now, { hour: '2-digit', minute: '2-digit' });
    setDebugInfo(prev => `${prev}\n[${timestamp}] ${message}`);
    console.log(`[WebRTC Polling] ${message}`);
  }, []);

  // Enumerate available media devices
  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(device => device.kind === 'videoinput');
      const microphones = devices.filter(device => device.kind === 'audioinput');
      
      setAvailableDevices(devices);
      addDebugInfo(`Found ${cameras.length} cameras, ${microphones.length} microphones`);
      
      // Set default devices if not already selected
      if (!selectedCamera && cameras.length > 0) {
        setSelectedCamera(cameras[0].deviceId);
        const cameraLabel = cameras[0].label || `Camera ${cameras[0].deviceId.slice(0, 8)}`;
        addDebugInfo(`Default camera: ${cameraLabel}`);
      }
      if (!selectedMicrophone && microphones.length > 0) {
        setSelectedMicrophone(microphones[0].deviceId);
        const micLabel = microphones[0].label || `Microphone ${microphones[0].deviceId.slice(0, 8)}`;
        addDebugInfo(`Default microphone: ${micLabel}`);
      }
    } catch (error) {
      addDebugInfo(`Device enumeration error: ${error}`);
    }
  }, [addDebugInfo, selectedCamera, selectedMicrophone]);

  // Test camera functionality
  const testCamera = useCallback(async () => {
    try {
      addDebugInfo('Testing camera...');
      const testStream = await navigator.mediaDevices.getUserMedia({
        video: selectedCamera ? { deviceId: selectedCamera } : {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        }
      });

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = testStream;
        localVideoRef.current.muted = true;
        await localVideoRef.current.play();
        addDebugInfo('Camera test successful - you should see yourself');
      }

      // Stop test stream after 5 seconds
      setTimeout(() => {
        testStream.getTracks().forEach(track => track.stop());
        addDebugInfo('Camera test ended');
        
        // Restore original stream if it exists
        if (localStream && localVideoRef.current) {
          localVideoRef.current.srcObject = localStream;
        }
      }, 5000);

    } catch (error) {
      addDebugInfo(`Camera test failed: ${error}`);
    }
  }, [selectedCamera, addDebugInfo, localStream]);

  // Switch camera device
  const switchCamera = useCallback(async (deviceId: string) => {
    try {
      setSelectedCamera(deviceId);
      addDebugInfo(`Switching to camera: ${deviceId}`);

      if (localStream && isVideoEnabled) {
        // Get new video stream with selected camera
        const newVideoStream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId }
        });

        const newVideoTrack = newVideoStream.getVideoTracks()[0];
        const oldVideoTrack = localStream.getVideoTracks()[0];

        if (oldVideoTrack) {
          localStream.removeTrack(oldVideoTrack);
          oldVideoTrack.stop();
        }

        localStream.addTrack(newVideoTrack);

        // Update local video element
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = localStream;
          await localVideoRef.current.play();
        }

        // Replace track in all peer connections
        Object.values(peerConnections.current).forEach(pc => {
          const sender = pc.getSenders().find(s => s.track?.kind === 'video');
          if (sender) {
            sender.replaceTrack(newVideoTrack);
          }
        });

        addDebugInfo('Camera switched successfully');
      }
    } catch (error) {
      addDebugInfo(`Camera switch failed: ${error}`);
    }
  }, [localStream, isVideoEnabled, addDebugInfo]);

  // Switch microphone device
  const switchMicrophone = useCallback(async (deviceId: string) => {
    try {
      setSelectedMicrophone(deviceId);
      addDebugInfo(`Switching to microphone: ${deviceId}`);

      if (localStream) {
        // Get new audio stream with selected microphone
        const newAudioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });

        const newAudioTrack = newAudioStream.getAudioTracks()[0];
        const oldAudioTrack = localStream.getAudioTracks()[0];

        if (oldAudioTrack) {
          // Preserve audio enabled state
          const wasEnabled = oldAudioTrack.enabled;
          localStream.removeTrack(oldAudioTrack);
          oldAudioTrack.stop();
          
          localStream.addTrack(newAudioTrack);
          newAudioTrack.enabled = wasEnabled;
        }

        // Replace track in all peer connections
        Object.values(peerConnections.current).forEach(pc => {
          const sender = pc.getSenders().find(s => s.track?.kind === 'audio');
          if (sender) {
            sender.replaceTrack(newAudioTrack);
          }
        });

        addDebugInfo('Microphone switched successfully');
      }
    } catch (error) {
      addDebugInfo(`Microphone switch failed: ${error}`);
    }
  }, [localStream, addDebugInfo]);

  // Get API base URL
  const getApiUrl = () => {
    return process.env.NODE_ENV === 'production' 
      ? window.location.origin 
      : `http://${window.location.hostname}:3002`;
  };

  // HTTP request with retry logic and exponential backoff
  const makeHttpRequest = useCallback(async (url: string, options: RequestInit, maxRetries = 3): Promise<Response> => {
    let lastError: Error | null = null;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Add timeout to prevent hanging requests
        const timeoutController = new AbortController();
        const timeoutId = setTimeout(() => timeoutController.abort(), 10000); // 10 second timeout
        
        const response = await fetch(url, {
          ...options,
          signal: timeoutController.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          retryCount.current = 0; // Reset retry count on success
          return response;
        } else if (response.status >= 500) {
          // Server error, retry
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        } else {
          // Client error, don't retry
          return response;
        }
      } catch (error) {
        lastError = error as Error;
        
        if (attempt < maxRetries) {
          // Exponential backoff: 1s, 2s, 4s
          const delay = Math.pow(2, attempt) * 1000;
          addDebugInfo(`Request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms: ${error}`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError || new Error('Request failed after all retries');
  }, [addDebugInfo]);

  // HTTP Polling for signaling with improved error handling
  const startPolling = useCallback(async () => {
    if (!authState.user || pollingIntervalRef.current) return;

    addDebugInfo('Starting HTTP polling for signaling');
    setConnectionStatus('Connected');
    retryCount.current = 0;

    const poll = async () => {
      // Skip polling if we're in the middle of reconnecting
      if (isReconnecting.current) return;

      try {
        const apiUrl = getApiUrl();
        
        // Safe URL construction with timestamp validation
        let sinceParam = '';
        try {
          sinceParam = lastPollTime.current.toISOString();
        } catch (timestampError) {
          // If timestamp is invalid, use current time
          // eslint-disable-next-line no-restricted-syntax
          lastPollTime.current = new Date();
          sinceParam = lastPollTime.current.toISOString();
          addDebugInfo(`Invalid lastPollTime, reset to current time: ${timestampError}`);
        }
        
        const response = await makeHttpRequest(
          `${apiUrl}/api/huddle-polling/poll/${channelId}/${authState.user!.id}?since=${sinceParam}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          },
          2 // Max 2 retries for polling requests
        );

        const data = await response.json();
        if (data.success) {
          // Validate timestamp before using it
          const serverTimestamp = data.timestamp;
          if (serverTimestamp && !isNaN(new Date(serverTimestamp).getTime())) {
            lastPollTime.current = new Date(serverTimestamp);
          } else {
            // Fallback to current time if server timestamp is invalid
            lastPollTime.current = new Date();
            addDebugInfo(`Invalid server timestamp received: ${serverTimestamp}, using current time`);
          }

          // Reset connection status if it was in error state (only log when actually recovering)
          if (lastConnectionStatus.current !== 'Connected') {
            lastConnectionStatus.current = 'Connected';
            setConnectionStatus('Connected');
            addDebugInfo('Connection recovered');
          }

          // Process new messages
          for (const message of data.messages) {
            await handleSignalingMessage(message);
          }

          // Update participants list - merge polling data with initial participants
          setParticipants(prev => {
            // ENHANCED CURRENT USER PERSISTENCE - Multiple fallback strategies
            let currentUser = prev.find(p => p.isCurrentUser);
            
            // Strategy 2: If missing but we have authState and localStream, recreate
            if (!currentUser && authState.user && localStream) {
              addDebugInfo('Current user missing from prev state, recreating from authState + localStream');
              currentUser = {
                id: authState.user.id,
                name: authState.user.name || 'You',
                avatar: authState.user.avatar,
                isAudioEnabled: isAudioEnabled,
                isVideoEnabled: isVideoEnabled,
                isScreenSharing: isScreenSharing,
                stream: localStream,
                isActive: true,
                isCurrentUser: true,
              };
            }
            
            // Strategy 3: If missing but we have authState (no localStream yet), create basic version
            else if (!currentUser && authState.user) {
              addDebugInfo('Current user missing, creating basic version from authState (no localStream)');
              currentUser = {
                id: authState.user.id,
                name: authState.user.name || 'You',
                avatar: authState.user.avatar,
                isAudioEnabled: isAudioEnabled,
                isVideoEnabled: isVideoEnabled,
                isScreenSharing: isScreenSharing,
                isActive: true,
                isCurrentUser: true,
              };
            }
            
            // Strategy 4: Last resort - if we still don't have current user, log warning
            else if (!currentUser) {
              addDebugInfo('WARNING: Cannot recreate current user - missing authState.user');
            }
            
            // Update current user media state if found in polling data (silently)
            if (currentUser) {
              const currentUserPollingData = data.participants.find((p: any) => p.userId === authState.user?.id);
              if (currentUserPollingData) {
                currentUser = {
                  ...currentUser,
                  isAudioEnabled: currentUserPollingData.isAudioEnabled,
                  isVideoEnabled: currentUserPollingData.isVideoEnabled,
                  isScreenSharing: currentUserPollingData.isScreenSharing,
                };
              }
            }
            
            // Merge polling participants with initial participants for complete team view
            const pollingParticipants = data.participants || [];
            const allKnownParticipants = [...initialParticipants];
            
            // Add polling participants that aren't in initial list
            pollingParticipants.forEach((p: any) => {
              if (!allKnownParticipants.find(ip => ip.id === p.userId)) {
                allKnownParticipants.push({
                  id: p.userId,
                  name: p.name,
                  email: `${p.name}@huddle.local`,
                  avatar: p.avatar,
                  isActive: true
                });
              }
            });
            
            // Create participant objects for all known participants (excluding current user)
            // CRITICAL FIX: Preserve existing stream from previous state!
            const remoteParticipants = allKnownParticipants
              .filter(ip => ip.id !== authState.user?.id)
              .map(ip => {
                const pollingData = pollingParticipants.find((p: any) => p.userId === ip.id);
                // CRITICAL: Find existing participant to preserve stream!
                const existingParticipant = prev.find(p => p.id === ip.id);
                return {
                  id: ip.id,
                  name: ip.name,
                  avatar: ip.avatar,
                  isAudioEnabled: pollingData?.isAudioEnabled || false,
                  isVideoEnabled: pollingData?.isVideoEnabled || false,
                  isScreenSharing: pollingData?.isScreenSharing || false,
                  isActive: pollingData ? true : false, // Only active if in polling data
                  isCurrentUser: false,
                  connection: peerConnections.current[ip.id],
                  // CRITICAL: Preserve stream from existing participant!
                  stream: existingParticipant?.stream
                };
              });

            const finalParticipants = currentUser ? [currentUser, ...remoteParticipants] : remoteParticipants;
            
            // CRITICAL FIX: Auto-create peer connections for active users without one
            // This handles the case where a user rejoins after leaving (join message might be missed)
            remoteParticipants.forEach(p => {
              if (p.isActive && !peerConnections.current[p.id]) {
                addDebugInfo(`Auto-creating peer connection for ${p.name} (active user without connection)`);
                // Don't await - create connection async
                createPeerConnection(p.id, { name: p.name, avatar: p.avatar, isAudioEnabled: p.isAudioEnabled, isVideoEnabled: p.isVideoEnabled, isScreenSharing: p.isScreenSharing }, true);
              }
            });
            
            return finalParticipants;
          });
        }
      } catch (error) {
        retryCount.current++;
        
        // Update connection status based on error frequency
        if (retryCount.current > 3 && lastConnectionStatus.current !== 'Connection issues') {
          lastConnectionStatus.current = 'Connection issues';
          setConnectionStatus('Connection issues');
          addDebugInfo(`Polling issues detected (${retryCount.current} consecutive failures)`);
          
          // Attempt to reconnect if too many failures
          if (retryCount.current > 10 && !isReconnecting.current) {
            addDebugInfo('Too many failures, attempting to reconnect...');
            await attemptReconnection();
          }
        }
        
        // Log errors more selectively based on frequency
        if (retryCount.current <= 3 || retryCount.current % 10 === 0) {
          addDebugInfo(`Polling error (${retryCount.current} consecutive): ${error}`);
        }
      }
    };

    // Initial poll
    await poll();

    // Set up interval polling - slightly longer interval for better serverless compatibility
    pollingIntervalRef.current = setInterval(poll, 1500); // Poll every 1.5 seconds
  }, [channelId, authState.user, addDebugInfo, connectionStatus, makeHttpRequest]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      addDebugInfo('Stopped HTTP polling');
    }
    setConnectionStatus('Disconnected');
  }, [addDebugInfo]);

  // Join huddle room
  const joinHuddle = useCallback(async () => {
    if (!authState.user) return;

    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/api/huddle-polling/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          channelId,
          userId: authState.user.id,
          name: authState.user.name,
          avatar: authState.user.avatar,
          isAudioEnabled,
          isVideoEnabled: false // Start with video disabled
        })
      });

      const data = await response.json();
      if (data.success) {
        addDebugInfo(`Joined huddle with ${data.participantCount} participants`);
        
        // Create peer connections for existing participants
        // NEW USER = NOT initiator (wait for offers from existing participants)
        // Existing participants will send offers when they see our 'join' message
        for (const participant of data.participants) {
          await createPeerConnection(participant.userId, participant, false); // false = wait for offer
        }

        // Start polling for messages
        startPolling();
      }
    } catch (error) {
      addDebugInfo(`Failed to join huddle: ${error}`);
    }
  }, [channelId, authState.user, isAudioEnabled, addDebugInfo, startPolling]);

  // Leave huddle room with improved error handling
  const leaveHuddle = useCallback(async () => {
    if (!authState.user || !channelId) {
      addDebugInfo('Skip leave - missing user or channelId');
      return;
    }

    try {
      stopPolling();
      
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/api/huddle-polling/leave`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          channelId,
          userId: authState.user.id
        })
      });

      if (response.ok) {
        addDebugInfo('Left huddle successfully');
      } else {
        // Don't throw on 400 errors during cleanup - already left or session expired
        const status = response.status;
        if (status === 400 || status === 404) {
          addDebugInfo(`Huddle leave returned ${status} (already left or session expired)`);
        } else {
          addDebugInfo(`Failed to leave huddle: HTTP ${status}`);
        }
      }
    } catch (error) {
      // Catch network errors but don't fail cleanup
      addDebugInfo(`Leave huddle network error (non-blocking): ${error}`);
    }
  }, [channelId, authState.user, stopPolling, addDebugInfo]);

  // Helper function to encode SDP for safe transmission (preserves \r\n)
  const encodeSdpData = useCallback((data: any): any => {
    if (!data) return data;
    
    // If this is an offer/answer with SDP, Base64 encode the SDP string
    if (data.sdp && typeof data.sdp === 'string') {
      return {
        ...data,
        sdp: btoa(unescape(encodeURIComponent(data.sdp))),
        _sdpEncoded: true
      };
    }
    
    return data;
  }, []);

  // Helper function to decode SDP after receiving
  const decodeSdpData = useCallback((data: any): any => {
    if (!data) return data;
    
    // If SDP was encoded, decode it
    if (data._sdpEncoded && data.sdp && typeof data.sdp === 'string') {
      try {
        const decoded = {
          ...data,
          sdp: decodeURIComponent(escape(atob(data.sdp)))
        };
        delete decoded._sdpEncoded;
        return decoded;
      } catch (error) {
        addDebugInfo(`Error decoding SDP: ${error}`);
        return data;
      }
    }
    
    return data;
  }, [addDebugInfo]);

  // Send signaling message
  const sendSignal = useCallback(async (type: string, data: any, toUserId?: string) => {
    if (!authState.user) return;

    try {
      // Encode SDP data for offer/answer to preserve \r\n line endings
      const encodedData = (type === 'offer' || type === 'answer') ? encodeSdpData(data) : data;
      
      const apiUrl = getApiUrl();
      await fetch(`${apiUrl}/api/huddle-polling/signal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          channelId,
          fromUserId: authState.user.id,
          toUserId,
          type,
          data: encodedData
        })
      });
    } catch (error) {
      addDebugInfo(`Failed to send ${type} signal: ${error}`);
    }
  }, [channelId, authState.user, addDebugInfo, encodeSdpData]);

  // Fetch ICE server config from backend (keeps TURN credentials off the client)
  const fetchIceServers = useCallback(async () => {
    try {
      const apiUrl = getApiUrl();
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/ice-servers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          rtcConfig.current = {
            iceServers: data.data.iceServers,
            iceCandidatePoolSize: data.data.iceCandidatePoolSize || 10,
            iceTransportPolicy: data.data.iceTransportPolicy || 'all',
          };
          addDebugInfo(`ICE servers loaded from backend: ${data.data.iceServers.length} servers`);
        }
      }
    } catch (error) {
      addDebugInfo(`Failed to fetch ICE servers from backend, using defaults: ${error}`);
    }
  }, [addDebugInfo]);

  // Create peer connection
  const createPeerConnection = useCallback(async (userId: string, userData: any, isInitiator: boolean) => {
    if (peerConnections.current[userId]) {
      return; // Connection already exists
    }

    const peerConnection = new RTCPeerConnection(rtcConfig.current);
    peerConnections.current[userId] = peerConnection;

    // Add participant
    setParticipants(prev => {
      const existing = prev.find(p => p.id === userId);
      if (existing) return prev;
      
      return [...prev, {
        id: userId,
        name: userData.name,
        avatar: userData.avatar,
        isAudioEnabled: userData.isAudioEnabled,
        isVideoEnabled: userData.isVideoEnabled,
        isScreenSharing: userData.isScreenSharing,
        connection: peerConnection,
        isActive: true,
        isCurrentUser: false,
      }];
    });

    // Add local stream to peer connection
    if (localStream) {
      localStream.getTracks().forEach(track => {
        peerConnection.addTrack(track, localStream);
      });
    }

    // Handle incoming stream
    peerConnection.ontrack = (event) => {
      addDebugInfo(`Received remote stream from ${userId}`);
      const [remoteStream] = event.streams;
      
      setParticipants(prev => prev.map(p => 
        p.id === userId ? { ...p, stream: remoteStream } : p
      ));

      // Update video element
      const videoElement = remoteVideoRefs.current[userId];
      if (videoElement && remoteStream) {
        videoElement.srcObject = remoteStream;
        videoElement.play().catch(console.error);
      }
    };

    // Handle ICE candidates
    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        sendSignal('ice-candidate', event.candidate, userId);
      }
    };

    // Handle connection state changes
    peerConnection.onconnectionstatechange = () => {
      addDebugInfo(`Connection state with ${userId}: ${peerConnection.connectionState}`);
      if (peerConnection.connectionState === 'failed') {
        addDebugInfo(`ICE connection failed for ${userId}, restarting ICE...`);
        peerConnection.restartIce();
      }
      if (peerConnection.connectionState === 'connected') {
        addDebugInfo(`✅ Successfully connected to ${userId}!`);
      }
    };

    // Handle ICE connection state changes
    peerConnection.oniceconnectionstatechange = () => {
      addDebugInfo(`ICE connection state with ${userId}: ${peerConnection.iceConnectionState}`);
    };

    // Handle ICE gathering state changes
    peerConnection.onicegatheringstatechange = () => {
      addDebugInfo(`ICE gathering state with ${userId}: ${peerConnection.iceGatheringState}`);
    };

    // Create offer if initiator
    if (isInitiator) {
      try {
        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);
        sendSignal('offer', offer, userId);
      } catch (error) {
        addDebugInfo(`Error creating offer for ${userId}: ${error}`);
      }
    }
  }, [localStream, addDebugInfo, sendSignal]);

  // Process buffered ICE candidates after remote description is set
  const processBufferedIceCandidates = useCallback(async (userId: string) => {
    const peerConnection = peerConnections.current[userId];
    const bufferedCandidates = iceCandidateBuffer.current[userId] || [];
    
    if (!peerConnection || bufferedCandidates.length === 0) return;
    
    addDebugInfo(`Processing ${bufferedCandidates.length} buffered ICE candidates for ${userId}`);
    
    for (const candidate of bufferedCandidates) {
      try {
        await peerConnection.addIceCandidate(candidate);
      } catch (error) {
        addDebugInfo(`Error adding buffered ICE candidate: ${error}`);
      }
    }
    
    // Clear the buffer
    iceCandidateBuffer.current[userId] = [];
  }, [addDebugInfo]);

  // Handle incoming offer with "Perfect Negotiation Pattern"
  // This handles "glare" - when both sides try to negotiate simultaneously
  const handleOffer = useCallback(async (fromUserId: string, offer: RTCSessionDescriptionInit) => {
    let peerConnection = peerConnections.current[fromUserId];
    const isNewConnection = !peerConnection;
    
    // Create peer connection if it doesn't exist
    if (!peerConnection) {
      addDebugInfo(`Creating NEW peer connection for offer from ${fromUserId}`);
      peerConnection = new RTCPeerConnection(rtcConfig.current);
      peerConnections.current[fromUserId] = peerConnection;
      
      // Add local stream
      if (localStream) {
        localStream.getTracks().forEach(track => {
          peerConnection!.addTrack(track, localStream!);
        });
      }
      
      // Handle ICE candidates
      peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          sendSignal('ice-candidate', event.candidate, fromUserId);
        }
      };
    } else {
      addDebugInfo(`Reusing EXISTING peer connection for offer from ${fromUserId}`);
    }
    
    // CRITICAL FIX: ALWAYS set ontrack handler (even for existing connections!)
    // This ensures we capture new tracks added during renegotiation
    peerConnection.ontrack = (event) => {
      addDebugInfo(`🎥 ontrack FIRED for ${fromUserId} - track kind: ${event.track.kind}`);
      const [remoteStream] = event.streams;
      if (remoteStream) {
        addDebugInfo(`🎥 Remote stream received: ${remoteStream.id}, tracks: ${remoteStream.getTracks().length}`);
        setParticipants(prev => {
          const updated = prev.map(p => 
            p.id === fromUserId ? { ...p, stream: remoteStream } : p
          );
          // If no participant matched, log it
          const found = prev.find(p => p.id === fromUserId);
          if (!found) {
            addDebugInfo(`⚠️ WARNING: No participant found with id ${fromUserId} - checking all IDs: ${prev.map(p => p.id).join(', ')}`);
          }
          return updated;
        });
      } else {
        addDebugInfo(`⚠️ ontrack fired but no stream in event.streams`);
      }
    };

    try {
      const state = peerConnection.signalingState;
      
      // PERFECT NEGOTIATION PATTERN: Handle "glare" (both sides sending offers)
      // "Polite" peer (lower ID) gives way, "Impolite" peer (higher ID) wins
      const weAreMakingOffer = makingOffer.current[fromUserId] || false;
      const offerCollision = weAreMakingOffer && state !== 'stable';
      
      // Determine if we are the "polite" peer (lower user ID = polite)
      const myId = authState.user?.id || '';
      const weArePolite = myId < fromUserId;
      
      if (offerCollision) {
        if (weArePolite) {
          // We're polite - rollback our offer and accept theirs
          addDebugInfo(`🔄 GLARE: We're polite, rolling back our offer to accept ${fromUserId}'s offer`);
          await peerConnection.setLocalDescription({ type: 'rollback' });
          makingOffer.current[fromUserId] = false;
        } else {
          // We're impolite - ignore their offer, keep ours
          addDebugInfo(`🔄 GLARE: We're impolite, ignoring ${fromUserId}'s offer (keeping ours)`);
          return;
        }
      }
      
      // Only proceed if we're in a valid state after potential rollback
      const newState = peerConnection.signalingState;
      if (newState !== 'stable' && newState !== 'have-local-offer') {
        addDebugInfo(`Skipping offer - invalid state after glare handling: ${newState}`);
        return;
      }
      
      await peerConnection.setRemoteDescription(offer);
      addDebugInfo(`Remote description (offer) set for ${fromUserId}`);
      
      // Process any buffered ICE candidates
      await processBufferedIceCandidates(fromUserId);
      
      const answer = await peerConnection.createAnswer();
      await peerConnection.setLocalDescription(answer);
      sendSignal('answer', answer, fromUserId);
      addDebugInfo(`Answer sent to ${fromUserId}`);
    } catch (error) {
      addDebugInfo(`Error handling offer from ${fromUserId}: ${error}`);
    }
  }, [addDebugInfo, sendSignal, localStream, processBufferedIceCandidates, authState.user?.id]);

  // Handle incoming answer
  const handleAnswer = useCallback(async (fromUserId: string, answer: RTCSessionDescriptionInit) => {
    const peerConnection = peerConnections.current[fromUserId];
    if (!peerConnection) {
      addDebugInfo(`No peer connection for answer from ${fromUserId}`);
      return;
    }

    try {
      // Check signaling state - only set remote answer if we have a local offer
      const state = peerConnection.signalingState;
      if (state !== 'have-local-offer') {
        addDebugInfo(`Skipping answer - invalid state: ${state} (expected have-local-offer)`);
        return;
      }
      
      await peerConnection.setRemoteDescription(answer);
      addDebugInfo(`Remote description (answer) set for ${fromUserId}`);
      
      // Process any buffered ICE candidates
      await processBufferedIceCandidates(fromUserId);
    } catch (error) {
      addDebugInfo(`Error handling answer from ${fromUserId}: ${error}`);
    }
  }, [addDebugInfo, processBufferedIceCandidates]);

  // Handle incoming ICE candidate
  const handleIceCandidate = useCallback(async (fromUserId: string, candidate: RTCIceCandidateInit) => {
    const peerConnection = peerConnections.current[fromUserId];
    
    // If no peer connection or remote description not set, buffer the candidate
    if (!peerConnection || !peerConnection.remoteDescription) {
      addDebugInfo(`Buffering ICE candidate from ${fromUserId} (no remote description yet)`);
      if (!iceCandidateBuffer.current[fromUserId]) {
        iceCandidateBuffer.current[fromUserId] = [];
      }
      iceCandidateBuffer.current[fromUserId].push(candidate);
      return;
    }

    try {
      await peerConnection.addIceCandidate(candidate);
    } catch (error) {
      addDebugInfo(`Error adding ICE candidate from ${fromUserId}: ${error}`);
    }
  }, [addDebugInfo]);

  // Update participant media state
  const updateParticipantMediaState = useCallback((userId: string, mediaState: any) => {
    setParticipants(prev => prev.map(p => 
      p.id === userId ? {
        ...p,
        isAudioEnabled: mediaState.isAudioEnabled,
        isVideoEnabled: mediaState.isVideoEnabled,
        isScreenSharing: mediaState.isScreenSharing,
      } : p
    ));
  }, []);

  // Renegotiate with a specific peer (needed when adding/removing tracks after connection is established)
  const renegotiateWithPeer = useCallback(async (userId: string) => {
    const peerConnection = peerConnections.current[userId];
    if (!peerConnection) {
      addDebugInfo(`Cannot renegotiate - no peer connection for ${userId}`);
      return;
    }

    try {
      addDebugInfo(`Starting renegotiation with ${userId}...`);
      
      // Create new offer
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);
      
      // Send offer via signaling
      sendSignal('offer', offer, userId);
      addDebugInfo(`Renegotiation offer sent to ${userId}`);
    } catch (error) {
      addDebugInfo(`Renegotiation failed with ${userId}: ${error}`);
    }
  }, [addDebugInfo, sendSignal]);

  // Close peer connection
  const closePeerConnection = useCallback((userId: string) => {
    const peerConnection = peerConnections.current[userId];
    if (peerConnection) {
      peerConnection.close();
      delete peerConnections.current[userId];
    }
    
    // Clear ICE candidate buffer
    delete iceCandidateBuffer.current[userId];

    setParticipants(prev => prev.filter(p => p.id !== userId));
  }, []);

  // Handle signaling messages (must be after all helper functions)
  const handleSignalingMessage = useCallback(async (message: SignalingMessage) => {
    const { id, type, fromUserId, data } = message;

    // Dedupe - skip already processed messages
    if (id && processedMessageIds.current.has(id)) {
      return;
    }
    if (id) {
      processedMessageIds.current.add(id);
      // Limit set size to prevent memory issues
      if (processedMessageIds.current.size > 1000) {
        const idsArray = Array.from(processedMessageIds.current);
        processedMessageIds.current = new Set(idsArray.slice(-500));
      }
    }

    switch (type) {
      case 'join':
        if (fromUserId !== authState.user?.id) {
          addDebugInfo(`User ${data.name} joined`);
          await createPeerConnection(fromUserId, data, true);
        }
        break;

      case 'leave':
        addDebugInfo(`User ${fromUserId} left`);
        // Clear ICE candidate buffer for this user
        delete iceCandidateBuffer.current[fromUserId];
        closePeerConnection(fromUserId);
        break;

      case 'offer':
        addDebugInfo(`Received offer from ${fromUserId}`);
        // Decode SDP if it was Base64 encoded
        await handleOffer(fromUserId, decodeSdpData(data));
        break;

      case 'answer':
        addDebugInfo(`Received answer from ${fromUserId}`);
        // Decode SDP if it was Base64 encoded
        await handleAnswer(fromUserId, decodeSdpData(data));
        break;

      case 'ice-candidate':
        // Don't log every ICE candidate to reduce noise
        await handleIceCandidate(fromUserId, data);
        break;

      case 'media-state':
        addDebugInfo(`Media state update from ${fromUserId}`);
        updateParticipantMediaState(fromUserId, data);
        break;
    }
  }, [authState.user?.id, addDebugInfo, createPeerConnection, closePeerConnection, handleOffer, handleAnswer, handleIceCandidate, updateParticipantMediaState, decodeSdpData]);

  // Attempt reconnection to huddle
  const attemptReconnection = useCallback(async () => {
    if (isReconnecting.current || !authState.user) return;

    isReconnecting.current = true;
    setConnectionStatus('Reconnecting...');
    addDebugInfo('Starting reconnection process...');

    try {
      // Stop current polling
      stopPolling();
      
      // Clear current connections
      Object.keys(peerConnections.current).forEach(userId => {
        closePeerConnection(userId);
      });

      // Wait a bit before reconnecting
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Attempt to rejoin
      await joinHuddle();
      
      addDebugInfo('Reconnection successful');
      setConnectionStatus('Connected');
      retryCount.current = 0;
    } catch (error) {
      addDebugInfo(`Reconnection failed: ${error}`);
      setConnectionStatus('Reconnection failed');
    } finally {
      isReconnecting.current = false;
    }
  }, [authState.user, stopPolling, closePeerConnection, joinHuddle, addDebugInfo]);

  // Broadcast media state
  const broadcastMediaState = useCallback(async () => {
    if (!authState.user) return;

    try {
      const apiUrl = getApiUrl();
      await fetch(`${apiUrl}/api/huddle-polling/media-state`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          channelId,
          userId: authState.user.id,
          isAudioEnabled,
          isVideoEnabled,
          isScreenSharing
        })
      });
    } catch (error) {
      addDebugInfo(`Failed to broadcast media state: ${error}`);
    }
  }, [channelId, authState.user, isAudioEnabled, isVideoEnabled, isScreenSharing, addDebugInfo]);

  // Initialize huddle
  const initializeHuddle = useCallback(async () => {
    if (streamSetupRef.current) {
      addDebugInfo('Huddle already initialized, skipping...');
      return;
    }
    
    try {
      setIsConnecting(true);
      setStreamStatus('Initializing...');
      addDebugInfo('Initializing huddle...');
      streamSetupRef.current = true;

      // Start with audio-only by default for privacy
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          deviceId: selectedMicrophone || undefined
        },
        video: false // Start with video disabled by default
      };
      
      setMediaConstraints(constraints);
      addDebugInfo(`Media constraints: ${JSON.stringify(constraints)}`);

      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        setIsVideoEnabled(false); // Video disabled by default
        setStreamStatus('Audio stream active');
        addDebugInfo('Audio-only stream acquired (video disabled by default)');
        addDebugInfo(`Stream tracks: ${stream.getTracks().map(t => `${t.kind}:${t.label}`).join(', ')}`);
        setLocalStream(stream);

        // Add current user to participants
        const currentUser: Participant = {
          id: authState.user?.id || 'unknown',
          name: authState.user?.name || 'You',
          avatar: authState.user?.avatar,
          isAudioEnabled,
          isVideoEnabled: false,
          isScreenSharing: false,
          stream,
          isActive: true,
          isCurrentUser: true,
        };

        setParticipants([currentUser]);
        addDebugInfo(`Added current user to participants: ${currentUser.name}`);

        // Set up local video element with better error handling
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
          localVideoRef.current.muted = true;
          
          // Force video to play with retry mechanism
          const playVideo = async (retries = 3) => {
            try {
              await localVideoRef.current?.play();
              addDebugInfo('Local video element playing successfully');
            } catch (error) {
              addDebugInfo(`Video play attempt failed: ${error}`);
              if (retries > 0) {
                addDebugInfo(`Retrying video play (${retries} attempts left)`);
                setTimeout(() => playVideo(retries - 1), 500);
              } else {
                addDebugInfo('All video play attempts failed - this is normal for audio-only streams');
              }
            }
          };
          
          // Start playing with retries
          await playVideo();
          addDebugInfo('Local video element set up with stream assignment');
        }

        // Join the huddle
        await joinHuddle();

        setIsConnecting(false);
        setStreamStatus('Ready');
        addDebugInfo('Huddle initialized successfully');

      } catch (mediaError) {
        setStreamStatus('Media error');
        addDebugInfo(`Media error: ${mediaError}`);
        setIsConnecting(false);
        alert('Could not access microphone. Check permissions.');
        return;
      }
    } catch (error) {
      addDebugInfo(`Initialization error: ${error}`);
      setIsConnecting(false);
      setStreamStatus('Initialization error');
      streamSetupRef.current = false;
    }
  }, [addDebugInfo, selectedMicrophone, authState.user, isAudioEnabled, joinHuddle]);

  // Cleanup function
  const cleanup = useCallback(async () => {
    addDebugInfo('Cleaning up huddle...');

    try {
      // Stop polling
      stopPolling();

      // Leave huddle
      await leaveHuddle();

      // Close all peer connections
      Object.keys(peerConnections.current).forEach(userId => {
        try {
          closePeerConnection(userId);
        } catch (error) {
          addDebugInfo(`Error closing peer connection for ${userId}: ${error}`);
        }
      });

      // Stop local stream
      if (localStream) {
        try {
          localStream.getTracks().forEach(track => {
            try {
              track.stop();
              addDebugInfo(`Stopped ${track.kind} track`);
            } catch (error) {
              addDebugInfo(`Error stopping ${track.kind} track: ${error}`);
            }
          });
          setLocalStream(null);
        } catch (error) {
          addDebugInfo(`Error stopping local stream: ${error}`);
        }
      }

      // Clear participants
      setParticipants([]);
      setConnectionStatus('Disconnected');
      streamSetupRef.current = false; // Reset so huddle can be reinitialized
      addDebugInfo('Cleanup completed successfully');
    } catch (error) {
      addDebugInfo(`Error during cleanup: ${error}`);
      streamSetupRef.current = false; // Reset even on error
    }
  }, [stopPolling, leaveHuddle, closePeerConnection, localStream, addDebugInfo]);

  // Toggle audio
  const toggleAudio = useCallback(() => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioEnabled(audioTrack.enabled);
        
        // Update current user in participants array
        setParticipants(prev => prev.map(p => 
          p.isCurrentUser ? { ...p, isAudioEnabled: audioTrack.enabled } : p
        ));
        
        broadcastMediaState();
        addDebugInfo(`Audio ${audioTrack.enabled ? 'enabled' : 'disabled'}`);
      }
    }
  }, [localStream, broadcastMediaState, addDebugInfo]);

  // Video element availability retry mechanism
  const waitForVideoElement = useCallback(async (maxRetries = 10, delay = 100): Promise<boolean> => {
    for (let i = 0; i < maxRetries; i++) {
      if (localVideoRef.current) {
        addDebugInfo(`Video element available after ${i} retries`);
        return true;
      }
      addDebugInfo(`Waiting for video element... retry ${i + 1}/${maxRetries}`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    addDebugInfo(`Video element not available after ${maxRetries} retries`);
    return false;
  }, [addDebugInfo]);

  // Toggle video with improved state synchronization and retry mechanism
  const toggleVideo = useCallback(async () => {
    try {
      addDebugInfo(`Toggle video called - current state: ${isVideoEnabled}`);
      
      if (isVideoEnabled) {
        // Turn off video
        addDebugInfo('Turning off video...');
        if (localStream) {
          const videoTrack = localStream.getVideoTracks()[0];
          if (videoTrack) {
            videoTrack.stop();
            localStream.removeTrack(videoTrack);
            addDebugInfo('Video track stopped and removed from localStream');
          }
        }
        
        // Update states AFTER video is actually stopped
        setIsVideoEnabled(false);
        
        // Update current user in participants array
        setParticipants(prev => prev.map(p => 
          p.isCurrentUser ? { ...p, isVideoEnabled: false } : p
        ));
        
        addDebugInfo('Video disabled successfully');
      } else {
        // Turn on video
        addDebugInfo('Turning on video...');
        
        // CRITICAL FIX: Wait for video element to be available
        const videoElementAvailable = await waitForVideoElement();
        if (!videoElementAvailable) {
          addDebugInfo('ERROR: Video element not available after retries - aborting video toggle');
          return;
        }
        
        try {
          const videoStream = await navigator.mediaDevices.getUserMedia({
            video: selectedCamera ? { deviceId: selectedCamera } : {
              width: { ideal: 1280 },
              height: { ideal: 720 },
              facingMode: 'user'
            }
          });
          addDebugInfo('Video stream acquired from camera');

          const videoTrack = videoStream.getVideoTracks()[0];
          if (!localStream) {
            addDebugInfo('ERROR: No localStream available for video track');
            videoTrack.stop();
            return;
          }

          if (!videoTrack) {
            addDebugInfo('ERROR: No video track available from stream');
            return;
          }

          // Add video track to localStream
          localStream.addTrack(videoTrack);
          addDebugInfo(`Video track added to localStream. Total tracks: ${localStream.getTracks().length}`);
          
          // ENHANCED: Video element setup with confirmed availability
          if (localVideoRef.current) {
            localVideoRef.current.srcObject = localStream;
            localVideoRef.current.muted = true;
            
            try {
              await localVideoRef.current.play();
              addDebugInfo('✅ Local video element playing successfully');
            } catch (playError) {
              addDebugInfo(`Local video play failed: ${playError}`);
              // Continue anyway, might still work
            }
          } else {
            addDebugInfo('❌ CRITICAL: localVideoRef.current is still null after retry mechanism');
          }

          // Add track to all peer connections and trigger renegotiation
          const peerIds = Object.keys(peerConnections.current);
          for (const peerId of peerIds) {
            const pc = peerConnections.current[peerId];
            if (!pc) continue;
            
            const sender = pc.getSenders().find((s: RTCRtpSender) => s.track?.kind === 'video');
            if (sender) {
              try {
                await sender.replaceTrack(videoTrack);
                addDebugInfo(`Replaced video track for ${peerId}`);
              } catch (err) {
                addDebugInfo(`Replace track failed for ${peerId}: ${err}`);
              }
            } else {
              pc.addTrack(videoTrack, localStream);
              addDebugInfo(`Added video track to ${peerId} - triggering renegotiation`);
              // CRITICAL: Renegotiate so remote peer learns about the new track
              await renegotiateWithPeer(peerId);
            }
          }

          // ONLY set state AFTER everything is confirmed working
          setIsVideoEnabled(true);
          
          // Update current user in participants array with confirmed stream
          setParticipants(prev => prev.map(p => 
            p.isCurrentUser ? { 
              ...p, 
              isVideoEnabled: true, 
              stream: localStream 
            } : p
          ));
          
          addDebugInfo(`✅ Video enabled successfully - localStream has ${localStream.getVideoTracks().length} video tracks`);
          
        } catch (videoError) {
          addDebugInfo(`Video stream error: ${videoError}`);
          // Reset state on error
          setIsVideoEnabled(false);
          setParticipants(prev => prev.map(p => 
            p.isCurrentUser ? { ...p, isVideoEnabled: false } : p
          ));
          throw videoError;
        }
      }

      // Broadcast state AFTER all local changes are confirmed
      await broadcastMediaState();
      
    } catch (error) {
      addDebugInfo(`Video toggle error: ${error}`);
      // Ensure consistent state on error
      setIsVideoEnabled(false);
      setParticipants(prev => prev.map(p => 
        p.isCurrentUser ? { ...p, isVideoEnabled: false } : p
      ));
    }
  }, [isVideoEnabled, localStream, selectedCamera, broadcastMediaState, addDebugInfo, waitForVideoElement]);

  // Toggle screen share
  const toggleScreenShare = useCallback(async () => {
    try {
      if (isScreenSharing) {
        // Stop screen sharing, return to camera
        if (isVideoEnabled) {
          await toggleVideo();
        }
        setIsScreenSharing(false);
        addDebugInfo('Screen sharing stopped');
      } else {
        // Start screen sharing
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true
        });

        const screenTrack = screenStream.getVideoTracks()[0];
        if (localStream && screenTrack) {
          // Replace existing video track
          const existingVideoTrack = localStream.getVideoTracks()[0];
          if (existingVideoTrack) {
            localStream.removeTrack(existingVideoTrack);
            existingVideoTrack.stop();
          }

          localStream.addTrack(screenTrack);

          // Update video element
          if (localVideoRef.current) {
            localVideoRef.current.srcObject = localStream;
            localVideoRef.current.play().catch(console.error);
          }

          // Replace track in all peer connections
          Object.values(peerConnections.current).forEach(pc => {
            const sender = pc.getSenders().find(s => s.track?.kind === 'video');
            if (sender) {
              sender.replaceTrack(screenTrack);
            } else {
              pc.addTrack(screenTrack, localStream);
            }
          });

          // Handle screen share end
          screenTrack.onended = () => {
            setIsScreenSharing(false);
            setIsVideoEnabled(false);
            broadcastMediaState();
          };

          setIsScreenSharing(true);
          setIsVideoEnabled(true);
          addDebugInfo('Screen sharing started');
        }
      }

      await broadcastMediaState();
    } catch (error) {
      addDebugInfo(`Screen share error: ${error}`);
    }
  }, [isScreenSharing, isVideoEnabled, localStream, toggleVideo, broadcastMediaState, addDebugInfo]);

  const handleLeaveHuddle = useCallback(() => {
    addDebugInfo('Leave huddle button clicked');
    cleanup();
    onClose();
  }, [cleanup, onClose, addDebugInfo]);

  // Effects - Only depend on open state to prevent re-render loops
  useEffect(() => {
    if (open) {
      addDebugInfo('Modal opened - fetching ICE servers then initializing huddle');
      enumerateDevices();
      // Fetch TURN credentials from backend first, then initialize
      fetchIceServers().then(() => initializeHuddle());
    } else {
      addDebugInfo('Modal closed - cleaning up');
      // Use setTimeout to defer cleanup until after React finishes unmounting
      // This prevents React Error #301 (target container is not a DOM element)
      const timeoutId = setTimeout(() => {
        cleanup();
      }, 0);
      
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Only open state - callbacks are stable

  // Enumerate devices on mount only
  useEffect(() => {
    enumerateDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // CRITICAL: Update remote video srcObject when participant streams change
  useEffect(() => {
    addDebugInfo(`useEffect triggered - ${participants.length} participants`);
    participants.forEach(participant => {
      if (participant.isCurrentUser) {
        addDebugInfo(`useEffect: Skipping current user ${participant.name}`);
        return;
      }
      
      addDebugInfo(`useEffect: Checking ${participant.name} - hasStream=${!!participant.stream}, isVideoEnabled=${participant.isVideoEnabled}`);
      
      if (!participant.stream) {
        addDebugInfo(`useEffect: ${participant.name} has NO stream`);
        return;
      }
      
      const videoTracks = participant.stream.getVideoTracks();
      addDebugInfo(`useEffect: ${participant.name} has ${videoTracks.length} video tracks`);
      
      const videoEl = remoteVideoRefs.current[participant.id];
      if (!videoEl) {
        addDebugInfo(`useEffect: NO video element for ${participant.name}`);
        return;
      }
      
      if (videoEl.srcObject !== participant.stream) {
        addDebugInfo(`useEffect: Setting srcObject for ${participant.name}`);
        videoEl.srcObject = participant.stream;
        setTimeout(() => {
          if (videoEl && videoEl.srcObject === participant.stream) {
            videoEl.play().catch(error => {
              addDebugInfo(`useEffect play error for ${participant.name}: ${error}`);
            });
          }
        }, 100);
      } else {
        addDebugInfo(`useEffect: srcObject already set for ${participant.name}`);
      }
    });
  }, [participants, addDebugInfo]);

  // Cleanup on component unmount only
  useEffect(() => {
    return () => {
      addDebugInfo('Component unmounting - final cleanup');
      // Clear video refs before cleanup to prevent DOM manipulation errors
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = null;
      }
      Object.values(remoteVideoRefs.current).forEach(videoEl => {
        if (videoEl) {
          videoEl.srcObject = null;
        }
      });
      remoteVideoRefs.current = {};
      
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on unmount

  return (
    <Dialog
      open={open}
      onClose={handleLeaveHuddle}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          backgroundColor: '#1a1a1a',
          color: 'white',
        },
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#2d2d2d', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">
            Huddle in #{channelName}
          </Typography>
          <Chip 
            label={`${participants.length} participant${participants.length !== 1 ? 's' : ''}`}
            size="small"
            sx={{ backgroundColor: '#4caf50', color: 'white' }}
          />
          <Chip 
            label={connectionStatus}
            size="small"
            color={connectionStatus === 'Connected' ? 'success' : 'default'}
          />
        </Box>
        {isConnecting && (
          <Typography variant="body2" color="text.secondary">
            Connecting...
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ 
        backgroundColor: '#1a1a1a', 
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}>
        {/* Video Grid */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'hidden',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          height: '100%'
        }}>
          {participants.map((participant) => (
            <Box
              key={participant.id}
              sx={{
                flex: participants.length === 1 ? '1 1 100%' : 
                      participants.length <= 4 ? '1 1 calc(50% - 8px)' : 
                      '1 1 calc(33.333% - 11px)',
                minHeight: participants.length === 1 ? '100%' : '200px',
                maxHeight: participants.length === 1 ? '100%' : '50%',
              }}
            >
              <Paper
                sx={{
                  height: '100%',
                  backgroundColor: '#2d2d2d',
                  border: '2px solid #444',
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {/* Enhanced video display logic with better validation and debugging */}
                {(() => {
                  // Enhanced video condition checking with detailed validation
                  let shouldShowVideo = false;
                  let debugReason = '';
                  
                  if (participant.isCurrentUser) {
                    // Current user video logic
                    const hasStream = !!localStream;
                    const hasVideoTrack = (localStream?.getVideoTracks()?.length || 0) > 0;
                    
                    // FIXED: Show video if we have actual tracks - don't rely on state
                    shouldShowVideo = hasStream && hasVideoTrack;
                    debugReason = `Current user: stream=${hasStream}, videoTrack=${hasVideoTrack}`;
                    
                    // Only log once when there's an actual problem (not on every render)
                    // This prevents console spam from React re-renders
                  } else {
                    // Remote participant video logic  
                    // CRITICAL FIX: Show video if we have a video track - don't rely on polling state!
                    // The polling state can be delayed, but if we have actual video tracks, show them!
                    const hasStream = !!participant.stream;
                    const hasVideoTrack = (participant.stream?.getVideoTracks()?.length || 0) > 0;
                    
                    // FIXED: Only check if tracks exist - polling state is unreliable
                    shouldShowVideo = hasStream && hasVideoTrack;
                    debugReason = `Remote ${participant.name}: stream=${hasStream}, videoTrack=${hasVideoTrack}`;
                  }
                  
                  return (
                    <>
                      {/* ALWAYS render video element to fix localVideoRef availability */}
                      <video
                        ref={participant.isCurrentUser
                          ? localVideoRef 
                          : (el: HTMLVideoElement | null) => {
                              if (el) {
                                remoteVideoRefs.current[participant.id] = el;
                                // CRITICAL: Only set srcObject if it's different to prevent play() abort errors
                                if (participant.stream && el.srcObject !== participant.stream) {
                                  addDebugInfo(`Setting remote video srcObject for ${participant.name}`);
                                  el.srcObject = participant.stream;
                                  // Delay play to let srcObject settle
                                  setTimeout(() => {
                                    if (el && el.srcObject === participant.stream) {
                                      el.play().catch(error => {
                                        addDebugInfo(`Remote video play failed for ${participant.name}: ${error}`);
                                      });
                                    }
                                  }, 100);
                                }
                              }
                            }
                        }
                        autoPlay
                        playsInline
                        muted={participant.isCurrentUser}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          opacity: shouldShowVideo ? 1 : 0,
                          display: shouldShowVideo ? 'block' : 'none',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          zIndex: shouldShowVideo ? 2 : 1,
                        }}
                        onLoadedData={() => {
                          if (participant.isCurrentUser) {
                            addDebugInfo('Current user video loaded and ready to play');
                          }
                        }}
                        onError={(e) => {
                          addDebugInfo(`Video error for ${participant.name}: ${e}`);
                        }}
                      />
                      
                      {/* Avatar shown when video is not available */}
                      <Avatar
                        src={participant.avatar ? (participant.avatar.startsWith('http') || participant.avatar.startsWith('/api') ? participant.avatar : `/api${participant.avatar}`) : undefined}
                        sx={{
                          width: 80,
                          height: 80,
                          fontSize: '2rem',
                          backgroundColor: '#4caf50',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: shouldShowVideo ? 1 : 2,
                        }}
                      >
                        {participant.name.charAt(0).toUpperCase()}
                      </Avatar>
                    </>
                  );
                })()}

                {/* Video disabled overlay for current user */}
                {participant.isCurrentUser && participant.stream && !participant.isVideoEnabled && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <VideocamOff sx={{ fontSize: 14, color: '#f44336' }} />
                    <Typography variant="caption" sx={{ color: 'white' }}>
                      Preview
                    </Typography>
                  </Box>
                )}

                {/* Participant Info Overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    left: 8,
                    right: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                    {participant.name}
                    {participant.id === authState.user?.id && ' (You)'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {!participant.isAudioEnabled && (
                      <MicOff sx={{ fontSize: 16, color: '#f44336' }} />
                    )}
                    {participant.isScreenSharing && (
                      <ScreenShare sx={{ fontSize: 16, color: '#4caf50' }} />
                    )}
                  </Box>
                </Box>
              </Paper>
            </Box>
          ))}
        </Box>

        {/* Controls */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            backgroundColor: '#2d2d2d',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Tooltip title={isAudioEnabled ? 'Mute' : 'Unmute'}>
            <IconButton
              onClick={toggleAudio}
              sx={{
                backgroundColor: isAudioEnabled ? '#4caf50' : '#f44336',
                color: 'white',
                '&:hover': {
                  backgroundColor: isAudioEnabled ? '#388e3c' : '#d32f2f',
                },
              }}
            >
              {isAudioEnabled ? <Mic /> : <MicOff />}
            </IconButton>
          </Tooltip>

          <Tooltip title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}>
            <IconButton
              onClick={toggleVideo}
              sx={{
                backgroundColor: isVideoEnabled ? '#4caf50' : '#666',
                color: 'white',
                '&:hover': {
                  backgroundColor: isVideoEnabled ? '#388e3c' : '#555',
                },
              }}
            >
              {isVideoEnabled ? <Videocam /> : <VideocamOff />}
            </IconButton>
          </Tooltip>

          <Tooltip title={isScreenSharing ? 'Stop sharing' : 'Share screen'}>
            <IconButton
              onClick={toggleScreenShare}
              sx={{
                backgroundColor: isScreenSharing ? '#ff9800' : '#666',
                color: 'white',
                '&:hover': {
                  backgroundColor: isScreenSharing ? '#f57c00' : '#555',
                },
              }}
            >
              {isScreenSharing ? <StopScreenShare /> : <ScreenShare />}
            </IconButton>
          </Tooltip>

          <Tooltip title={isSpeakerEnabled ? 'Mute speaker' : 'Unmute speaker'}>
            <IconButton
              onClick={() => setIsSpeakerEnabled(!isSpeakerEnabled)}
              sx={{
                backgroundColor: isSpeakerEnabled ? '#4caf50' : '#666',
                color: 'white',
                '&:hover': {
                  backgroundColor: isSpeakerEnabled ? '#388e3c' : '#555',
                },
              }}
            >
              {isSpeakerEnabled ? <VolumeUp /> : <VolumeOff />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Settings">
            <IconButton
              onClick={() => setShowSettings(!showSettings)}
              sx={{
                backgroundColor: showSettings ? '#2196f3' : '#666',
                color: 'white',
                '&:hover': {
                  backgroundColor: showSettings ? '#1976d2' : '#555',
                },
              }}
            >
              <Settings />
            </IconButton>
          </Tooltip>

          <Tooltip title="Leave huddle">
            <IconButton
              onClick={handleLeaveHuddle}
              sx={{
                backgroundColor: '#f44336',
                color: 'white',
                ml: 2,
                '&:hover': {
                  backgroundColor: '#d32f2f',
                },
              }}
            >
              <CallEnd />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Settings Panel */}
        {showSettings && (
          <Paper
            sx={{
              backgroundColor: '#2d2d2d',
              p: 3,
              borderRadius: 2,
              border: '1px solid #444',
            }}
          >
            <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
              HTTP Polling Settings & Debug
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Device Selection */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'white', mb: 2 }}>
                  Device Settings:
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  {/* Camera Selection */}
                  <FormControl size="small" fullWidth>
                    <InputLabel sx={{ color: '#ccc' }}>Camera</InputLabel>
                    <Select
                      value={selectedCamera}
                      onChange={(e) => switchCamera(e.target.value)}
                      label="Camera"
                      sx={{
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#666',
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#ccc',
                        },
                      }}
                    >
                      {availableDevices
                        .filter(device => device.kind === 'videoinput')
                        .map((device) => (
                          <MenuItem key={device.deviceId} value={device.deviceId}>
                            {device.label || `Camera ${device.deviceId.slice(0, 8)}...`}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  {/* Microphone Selection */}
                  <FormControl size="small" fullWidth>
                    <InputLabel sx={{ color: '#ccc' }}>Microphone</InputLabel>
                    <Select
                      value={selectedMicrophone}
                      onChange={(e) => switchMicrophone(e.target.value)}
                      label="Microphone"
                      sx={{
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#666',
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#ccc',
                        },
                      }}
                    >
                      {availableDevices
                        .filter(device => device.kind === 'audioinput')
                        .map((device) => (
                          <MenuItem key={device.deviceId} value={device.deviceId}>
                            {device.label || `Microphone ${device.deviceId.slice(0, 8)}...`}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Device Control Buttons */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CameraAlt />}
                    onClick={testCamera}
                    sx={{
                      color: '#4caf50',
                      borderColor: '#4caf50',
                      '&:hover': {
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        borderColor: '#388e3c',
                      },
                    }}
                  >
                    Test Camera
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Refresh />}
                    onClick={enumerateDevices}
                    sx={{
                      color: '#2196f3',
                      borderColor: '#2196f3',
                      '&:hover': {
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        borderColor: '#1976d2',
                      },
                    }}
                  >
                    Refresh Devices
                  </Button>
                </Box>
              </Box>

              {/* Connection Status */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'white', mb: 1 }}>
                  Connection Status:
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Chip
                    label={`HTTP Polling: ${connectionStatus}`}
                    color={connectionStatus === 'Connected' ? 'success' : 'default'}
                    size="small"
                  />
                  <Chip
                    label={`Peer Connections: ${Object.keys(peerConnections.current).length}`}
                    color={Object.keys(peerConnections.current).length > 0 ? 'success' : 'default'}
                    size="small"
                  />
                  <Chip
                    label={`Audio: ${isAudioEnabled ? 'On' : 'Off'}`}
                    color={isAudioEnabled ? 'success' : 'default'}
                    size="small"
                  />
                  <Chip
                    label={`Video: ${isVideoEnabled ? 'On' : 'Off'}`}
                    color={isVideoEnabled ? 'success' : 'default'}
                    size="small"
                  />
                  <Chip
                    label={`Local Stream: ${localStream ? 'Active' : 'Inactive'}`}
                    color={localStream ? 'success' : 'error'}
                    size="small"
                  />
                  <Chip
                    label={`Stream Status: ${streamStatus}`}
                    color={streamStatus === 'Ready' ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Box>

              {/* Stream Information */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'white', mb: 1 }}>
                  Stream Information:
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  {localStream && (
                    <>
                      {localStream.getAudioTracks().map((track, index) => (
                        <Chip
                          key={`audio-${index}`}
                          label={`Audio: ${track.label || 'Unknown'} (${track.enabled ? 'Enabled' : 'Disabled'})`}
                          size="small"
                          color={track.enabled ? 'success' : 'default'}
                        />
                      ))}
                      {localStream.getVideoTracks().map((track, index) => (
                        <Chip
                          key={`video-${index}`}
                          label={`Video: ${track.label || 'Unknown'} (${track.enabled ? 'Enabled' : 'Disabled'})`}
                          size="small"
                          color={track.enabled ? 'success' : 'default'}
                        />
                      ))}
                      <Chip
                        label={`Devices Found: ${availableDevices.length}`}
                        size="small"
                        color="info"
                      />
                    </>
                  )}
                </Box>
              </Box>

              {/* Debug Info */}
              {debugInfo && (
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'white', mb: 1 }}>
                    Debug Information:
                  </Typography>
                  <TextField
                    multiline
                    rows={6}
                    value={debugInfo}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                      sx: {
                        color: '#00ff00',
                        backgroundColor: '#000',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#666',
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button
                      onClick={() => setDebugInfo('')}
                      size="small"
                      sx={{ color: '#f44336' }}
                    >
                      Clear Debug
                    </Button>
                    <Button
                      onClick={attemptReconnection}
                      size="small"
                      sx={{ color: '#ff9800' }}
                    >
                      Force Reconnect
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        )}
      </DialogContent>
    </Dialog>
  );
};
